import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { SlidingPanel } from '@wix/wix-ui-premium-purchase';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { TransitionProps } from 'react-transition-group/Transition';
import { PlansPage } from '../PlansPage';
import { SubscriptionPage } from '../../../pages/SubscriptionPage/SubscriptionPage';
import { RootStore, withInjectedRootStore } from 'providers';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { Experiments } from 'enums';
import { Faq, FaqType } from '../../Faq';
import { AppController } from '../../App/App.controller';
import s from '../../App/App.scss';
import layoutStyles from './AppLayout.scss';
import { CheckoutPage } from '../../../pages/CheckoutPage/CheckoutPage';
import { useEnterpriseModalContext } from 'providers/EnterpriseModalContextProvider';
import { Title } from '../Title/Title';
import { SwitchPlanNotificationBar } from '../../SwitchPlanNotificationBar/SwitchPlanNotificationBar';
import { RequestCallModal } from 'components/RequestCall/Modal/RequestCallModal';
import { Box } from '@wix/design-system';
import { SaleBanner } from 'components/AppTitle/SaleBannerV2/SaleBanner';
import CycleSelector from '../../../pages/PlansPage/CycleSelector/CycleSelector';
import { AdvancedPlansProductData, ProductData } from '../../../types';
import { Step } from '../../../enums/enums';
import { SubscriptionPageController } from '../../../pages/SubscriptionPage/SubscriptionPage.controller';
import {
  InjectedGTMLoggersProps,
  withGtmLogger,
} from 'components/HOCs/withGtmLogger';

export interface AppLayoutProps
  extends InjectedExperimentsProps,
    InjectedGTMLoggersProps {
  rootStore: RootStore;
}
const animationTimeouts: TransitionProps['timeout'] = {
  enter: 600,
  exit: 300,
};

const AppLayoutComponent: React.FC<AppLayoutProps> = (props) => {
  const controller = React.useMemo(
    () => new AppController(props),
    [props.rootStore.appStore],
  );
  const { onCycleSelect, onSelectProduct, fetchOrderPageFunction } = controller;
  const { isModalOpen, clickSource, closeModal } = useEnterpriseModalContext();
  const { theme } = usePageModeContext();
  const { experiments } = props;

  const {
    currentStep,
    handleOnBackButtonClick,
    isWizardHidden,
    isDowngradePlanOfDeclineChargeService,
    appContext: { currentPlanDetails, biLogger },
    isInSale,
    isSwitchPlan,
    isAnonymous,
    removeUnloadListener,
  } = props.rootStore.appStore;

  if (isAnonymous) {
    removeUnloadListener();
  }

  const shouldRenderSwitchPlanNotification = currentPlanDetails && isSwitchPlan;
  const displaySaleBubble =
    isInSale && experiments.enabled(Experiments.SunriseV2_EnableSaleBubble);
  const isCycleSelectorInPlansPage = experiments.enabled(
    Experiments.CycleSelectorsInProductsPage,
  );

  const subscriptionPageController = React.useMemo(
    () =>
      isCycleSelectorInPlansPage
        ? new SubscriptionPageController({
            rootStore: props.rootStore,
            reportGTM: props.reportGTM,
            experiments: props.experiments,
          })
        : undefined,
    [],
  );

  const handleSelectProduct = React.useCallback(
    (product: ProductData | AdvancedPlansProductData) => {
      if (isCycleSelectorInPlansPage) {
        onSelectProduct(product, undefined, false);
        subscriptionPageController?.onCycleSelect();
      } else {
        onSelectProduct(product);
      }
    },
    [onSelectProduct, onCycleSelect, isCycleSelectorInPlansPage],
  );

  return (
    <div className={classNames(s.headerAndContent, theme)}>
      {shouldRenderSwitchPlanNotification && <SwitchPlanNotificationBar />}
      {displaySaleBubble && (
        <Box paddingTop='SP4' align='center'>
          <SaleBanner />
        </Box>
      )}
      <Title
        currentStep={currentStep}
        showBackButton={!isWizardHidden}
        onBack={handleOnBackButtonClick}
        isSecondStepDisabled={isDowngradePlanOfDeclineChargeService}
      />
      {isCycleSelectorInPlansPage && currentStep === Step.Plans && (
        <CycleSelector />
      )}
      <div
        className={classNames(
          layoutStyles.contentContainer,
          s.transitionContainer,
          s.panel,
          s.withBackgroundColor,
          theme,
        )}
      >
        <SlidingPanel
          activeChildIndex={currentStep}
          timeout={animationTimeouts}
        >
          <PlansPage
            className={layoutStyles.plansPageContainer}
            onSelectProduct={handleSelectProduct}
          />
          {!isCycleSelectorInPlansPage && (
            <SubscriptionPage
              className={layoutStyles.subscriptionContainer}
              data-hook='subscription-page'
              onCycleSelect={onCycleSelect}
            />
          )}
          <CheckoutPage
            className={layoutStyles.checkoutContainer}
            fetchOrderPageData={fetchOrderPageFunction}
          />
        </SlidingPanel>
        <Faq faqType={FaqType.All} />
        <RequestCallModal
          onCancelClick={() => {
            biLogger.logRequestCallPopupCTA({
              clickType: 'close',
            });
            closeModal();
          }}
          isOpen={isModalOpen}
          clickSource={clickSource}
        />
      </div>
    </div>
  );
};

export const AppLayout = withGtmLogger(
  withInjectedRootStore(withExperiments(observer(AppLayoutComponent))),
);
